import { useTranslation } from "@refinedev/core";
import Input from "components/input";
import { Label } from "components/label";
import { IBuildEventPageForm, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { useFormContext } from "react-hook-form";

export const MultilingualContent = ({
  isEdit,
  id,
}: {
  isEdit: boolean;
  id: string | null;
}) => {
  const { translate } = useTranslation();
  const {
    register,
    getFieldState,
    getValues,
    formState: { errors },
  } = useFormContext<IBuildEventPageForm>(); // retrieve all hook methods

  return (
    <div className="box-container">
      {!isEdit ? (
        (!getFieldState("multilingual_content").isTouched ||
          errors["multilingual_content"]) &&
        !id ? (
          <p>{translate("eventSetup.eventInfo.multilingualContent.note")}</p>
        ) : (
          <div className="flex flex-col gap-y-4 text-sm">
            <div className="flex flex-col sm:flex-row gap-2">
              <p className="shrink-0 sm:w-48 font-bold">
                {translate(
                  "eventSetup.eventInfo.multilingualContent.eventName"
                )}
                :
              </p>
              <div className="flex flex-col gap-y-3">
                {getValues("languages").map((language, index) => (
                  <p key={index}>
                    <img
                      src={`/images/flags/circle-${language}.png`}
                      alt="Flag icon"
                      className="inline mr-2.5"
                    />
                    {getValues("multilingual_content").name[language]}
                  </p>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <p className="shrink-0 sm:w-48 font-bold">
                {translate(
                  "eventSetup.eventInfo.multilingualContent.eventPlace"
                )}
                :
              </p>
              <div className="flex flex-col gap-y-3">
                {getValues("languages").map((language, index) => (
                  <p key={index}>
                    <img
                      src={`/images/flags/circle-${language}.png`}
                      alt="Flag icon"
                      className="inline mr-2.5"
                    />
                    {getValues("multilingual_content").place[language]}
                  </p>
                ))}
              </div>
            </div>
            {id ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <p className="shrink-0 sm:w-48 font-bold">
                  {translate(
                    "eventSetup.eventInfo.multilingualContent.displayOrganizationName"
                  )}
                  :
                </p>
                <div className="flex flex-col gap-y-3">
                  {getValues("languages").map((language, index) => (
                    <p key={index}>
                      <img
                        src={`/images/flags/circle-${language}.png`}
                        alt="Flag icon"
                        className="inline mr-2.5"
                      />
                      {
                        getValues("multilingual_content").merchant_name[
                          language
                        ]
                      }
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        )
      ) : (
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
          <div className="col-span-full">
            <Label
              required
              id="event_name_en"
              name={translate(
                "eventSetup.eventInfo.multilingualContent.eventName"
              )}
            />
            <div className="flex flex-col gap-y-4">
              {getValues("languages").map(
                (lang: SupportedLanguage, index: number) => (
                  <div key={lang}>
                    <div className="relative">
                      <Input
                        id={`event_name_${lang}`}
                        type="text"
                        placeholder={`${translate(
                          "eventSetup.eventInfo.multilingualContent.eventName"
                        )} (${lang})`}
                        className="pr-11"
                        error={get(
                          errors,
                          `multilingual_content.name.${lang}`,
                          ""
                        )}
                        {...register(`multilingual_content.name.${lang}`)}
                      />
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="absolute top-1/2 right-3 -translate-y-1/2"
                      />
                    </div>
                    {get(errors, `multilingual_content.name.${lang}`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `multilingual_content.name.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                )
              )}
            </div>
          </div>

          <div className="col-span-full">
            <Label
              required
              id="event_place_en"
              name={translate(
                "eventSetup.eventInfo.multilingualContent.eventPlace"
              )}
            />
            <div className="flex flex-col gap-y-4">
              {getValues("languages").map(
                (lang: SupportedLanguage, index: number) => (
                  <div key={index}>
                    <div className="relative">
                      <Input
                        id={`event_place_${lang}`}
                        type="text"
                        placeholder={`${translate(
                          "eventSetup.eventInfo.multilingualContent.eventPlace"
                        )} (${lang})`}
                        className="pr-11"
                        error={get(
                          errors,
                          `multilingual_content.place.${lang}`,
                          ""
                        )}
                        {...register(`multilingual_content.place.${lang}`)}
                      />
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="absolute top-1/2 right-3 -translate-y-1/2"
                      />
                    </div>
                    {get(errors, `multilingual_content.place.${lang}`, "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `multilingual_content.place.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                )
              )}
            </div>
          </div>
          {id ? (
            <div className="col-span-full">
              <Label
                id="merchant_name_en"
                name={translate(
                  "eventSetup.eventInfo.multilingualContent.displayOrganizationName"
                )}
              />
              <div className="flex flex-col gap-y-4">
                {getValues("languages").map(
                  (lang: SupportedLanguage, index: number) => (
                    <div key={index}>
                      <div className="relative">
                        <Input
                          id={`merchant_name_${lang}`}
                          type="text"
                          placeholder={`${translate(
                            "eventSetup.eventInfo.multilingualContent.displayOrganizationName"
                          )} (${lang})`}
                          className="pr-11"
                          error={get(
                            errors,
                            `multilingual_content.merchant_name.${lang}`,
                            ""
                          )}
                          {...register(
                            `multilingual_content.merchant_name.${lang}`
                          )}
                        />
                        <img
                          src={`/images/flags/circle-${lang}.png`}
                          alt="Language Icon"
                          className="absolute top-1/2 right-3 -translate-y-1/2"
                        />
                      </div>
                      {get(
                        errors,
                        `multilingual_content.merchant_name.${lang}`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `multilingual_content.merchant_name.${lang}.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  )
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </div>
      )}
    </div>
  );
};
